import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUsers, deleteUser, updateUser } from '../../services/userService';
import { getSchools } from '../../services/schoolService';
import { getAllCities, getAllStates } from '../../services/locationService';
import Loader from '../common/Loader';
import ConfirmModal from '../common/ConfirmModal';
import Alert from '../common/Alert';
import ProfileAvatar from '../common/ProfileAvatar';
import { useAuth } from '../../context/AuthContext';

const ROLE_MAP = { 1: 'SPED Director', 2: 'SPED Teacher' };

const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
  inactive: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
  suspended: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
};

function formatMobile(val) {
  if (!val) return null;
  const s = String(val);
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return s;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function exportToCSV(users) {
  const headers = [
    'First Name', 'Middle Name', 'Last Name', 'Email', 'Mobile',
    'Role', 'Status', 'City', 'State', 'Zipcode',
    'Address Line 1', 'Address Line 2', 'Hire Date', 'Termination Date', 'Created At',
  ];

  const rows = users.map((u) => [
    u.first_name || '',
    u.middle_name || '',
    u.last_name || '',
    u.email || '',
    u.mobile || '',
    u.role_name || ROLE_MAP[u.role] || '',
    u.is_active || '',
    u.city || '',
    u.state || '',
    u.zipcode || '',
    u.address_line1 || '',
    u.address_line2 || '',
    u.hire_date ? formatDate(u.hire_date) : '',
    u.termination_date ? formatDate(u.termination_date) : '',
    u.created_at ? formatDate(u.created_at) : '',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function UserList() {
  const { user: currentUser } = useAuth();
  const isDirector = currentUser?.role === 1;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [schools, setSchools] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Pagination & meta
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, total_pages: 0 });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    state: '',
    city: '',
    status: '',
    school_id: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const menuRefs = useRef({});
  const navigate = useNavigate();
  const searchDebounce = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (openMenuId !== null) {
        const ref = menuRefs.current[openMenuId];
        if (ref && !ref.contains(e.target)) setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const fetchUsers = useCallback(async (params = {}, page = 1) => {
    setLoading(true);
    try {
      const queryParams = { page, limit: pagination.limit, ...params };
      // Remove empty values
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] === '' || queryParams[k] === null || queryParams[k] === undefined) {
          delete queryParams[k];
        }
      });
      const { data } = await getUsers(queryParams);
      setUsers(data.data?.users || []);
      setPagination({
        total: data.data?.total || 0,
        page: data.data?.page || 1,
        limit: data.data?.limit || 10,
        total_pages: data.data?.total_pages || 0,
      });
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchUsers();
    if (isDirector) {
      getSchools({ page: 1, limit: 100, status: 'active' })
        .then(({ data }) => setSchools(data.data?.schools || []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    getAllStates()
      .then((res) => setStates(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAllCities(filters.state || null)
      .then((res) => setCities(res.data?.data || []))
      .catch(() => setCities([]));
  }, [filters.state]);

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    if (key === 'state') updated.city = '';
    setFilters(updated);
    setAppliedFilters(updated);
    fetchUsers(updated, 1);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, search: value }));
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      const newApplied = { ...appliedFilters, search: value };
      setAppliedFilters(newApplied);
      fetchUsers(newApplied, 1);
    }, 400);
  };

  const clearFilters = () => {
    const reset = { search: '', role: '', state: '', city: '', status: '', school_id: '' };
    setFilters(reset);
    setAppliedFilters({});
    fetchUsers({}, 1);
  };

  const activeFilterCount = Object.entries(appliedFilters).filter(
    ([k, v]) => k !== 'search' && v !== '' && v !== null && v !== undefined
  ).length;

  const handlePageChange = (newPage) => {
    fetchUsers(appliedFilters, newPage);
  };

  const handleExport = () => {
    exportToCSV(users);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteUser(confirmDelete);
      setUsers((prev) => prev.filter((u) => u.id !== confirmDelete));
      setAlert({ type: 'success', message: 'User deleted successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete user.' });
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const handleStatusChange = async () => {
    if (!confirmStatus) return;
    const { id, nextStatus } = confirmStatus;
    setStatusUpdating(true);
    try {
      const { data } = await updateUser(id, { is_active: nextStatus });
      const updatedStatus = data.data?.is_active ?? nextStatus;
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, is_active: updatedStatus } : u))
      );
      setAlert({
        type: 'success',
        message: updatedStatus === 'active'
          ? 'User activated successfully.'
          : 'User deactivated successfully.',
      });
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.response?.data?.detail || 'Failed to update user status.',
      });
    } finally {
      setStatusUpdating(false);
      setConfirmStatus(null);
    }
  };

  const toggleMenu = (id) => setOpenMenuId((prev) => (prev === id ? null : id));

  return (
    <div className="space-y-5">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        open={!!confirmStatus}
        title={confirmStatus?.nextStatus === 'active' ? 'Activate User' : 'Deactivate User'}
        message={
          confirmStatus?.nextStatus === 'active'
            ? `Activate ${confirmStatus?.name}? They will be able to sign in again.`
            : `Deactivate ${confirmStatus?.name}? They will no longer be able to sign in.`
        }
        onConfirm={handleStatusChange}
        onCancel={() => setConfirmStatus(null)}
        confirmText={confirmStatus?.nextStatus === 'active' ? 'Activate' : 'Deactivate'}
        variant={confirmStatus?.nextStatus === 'active' ? 'info' : 'danger'}
        loading={statusUpdating}
      />

      {/* Page header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
            {isDirector ? 'Manage all SPED staff accounts' : 'View and update your profile'}
            {pagination.total > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs font-medium">
                {pagination.total} total
              </span>
            )}
          </p>
        </div>
        {isDirector && (
        <div className="flex items-center gap-2">
          {/* Export CSV */}
          <button
            onClick={handleExport}
            disabled={users.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600 hover:text-gray-900 dark:hover:text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="hidden sm:inline">Export CSV</span>
          </button>

          <Link
            to="/users/add"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Add User</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </div>
        )}
      </div>

      {/* Inline filters row */}
      {isDirector && (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 w-full sm:min-w-[180px] sm:w-auto">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>

        <select
          value={filters.role}
          onChange={(e) => handleFilterChange('role', e.target.value)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">All Roles</option>
          <option value="1">SPED Director</option>
          <option value="2">SPED Teacher</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>

        <select
          value={filters.school_id}
          onChange={(e) => handleFilterChange('school_id', e.target.value)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">All Schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>{s.school_name}</option>
          ))}
        </select>

        <select
          aria-label="State"
          value={filters.state}
          onChange={(e) => handleFilterChange('state', e.target.value)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">All States</option>
          {states.map((s) => (
            <option key={s.state_code} value={s.state_code}>{s.state_name}</option>
          ))}
        </select>

        <select
          aria-label="City"
          title={filters.state ? 'Cities in the selected state' : 'All cities'}
          value={filters.city}
          onChange={(e) => handleFilterChange('city', e.target.value)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">All Cities</option>
          {cities.map((c) => (
            <option key={c.city} value={c.city}>{c.city}</option>
          ))}
        </select>

        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            title="Clear all filters"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      )}

      {/* Table card */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl">
        {loading ? (
          <Loader text="Loading users..." />
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
            <button onClick={() => fetchUsers(appliedFilters)} className="mt-3 text-indigo-600 dark:text-indigo-400 text-sm underline">
              Try again
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-slate-400 text-sm font-medium">No users found</p>
            <p className="text-gray-400 dark:text-slate-500 text-xs mt-1">
              {activeFilterCount > 0 ? 'Try adjusting your filters' : 'Add a new user to get started'}
            </p>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="mt-3 text-indigo-600 dark:text-indigo-400 text-sm underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-visible">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60">
                    <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">User</th>
                    <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider hidden md:table-cell">Location</th>
                    <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider hidden sm:table-cell">Role</th>
                    <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-center px-3 sm:px-5 py-3 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50/80 dark:hover:bg-slate-700/20 transition-colors">
                      {/* User — avatar + name + email + phone */}
                      <td className="px-3 sm:px-5 py-3 sm:py-4">
                        <div className="flex items-center gap-3">
                          <Link to={`/users/${u.id}`} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-white dark:ring-slate-800 hover:ring-indigo-300 dark:hover:ring-indigo-500 transition-all cursor-pointer">
                            <ProfileAvatar
                              type="user"
                              id={u.id}
                              variant="light"
                              hasProfileImage={u.has_profile_image}
                              fetchImage={!!u.has_profile_image}
                              lazyImage
                            />
                          </Link>
                          <div className="min-w-0">
                            <Link to={`/users/${u.id}`} className="text-gray-900 dark:text-white font-semibold text-sm truncate block hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              {[u.first_name, u.middle_name, u.last_name].filter(Boolean).join(' ')}
                            </Link>
                            <p className="text-gray-400 dark:text-slate-500 text-xs truncate mt-0.5">{u.email}</p>
                            {u.mobile && (
                              <p className="text-gray-400 dark:text-slate-500 text-xs mt-0.5">{formatMobile(u.mobile)}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Location — city + state */}
                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden md:table-cell">
                        {u.city || u.state ? (
                          <div>
                            <p className="text-gray-700 dark:text-slate-300 text-sm font-medium">
                              {[u.city, u.state].filter(Boolean).join(', ')}
                            </p>
                            {u.zipcode && (
                              <p className="text-gray-400 dark:text-slate-500 text-xs mt-0.5">{u.zipcode}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-slate-600 text-sm">—</span>
                        )}
                      </td>

                      {/* Role */}
                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden sm:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm block">
                          {u.role_name || ROLE_MAP[u.role] || '—'}
                        </span>
                        {u.school_name && (
                          <span className="text-gray-400 dark:text-slate-500 text-xs mt-0.5 block">{u.school_name}</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 sm:px-5 py-3 sm:py-4">
                        <span className={`inline-block px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-medium border capitalize ${statusColors[u.is_active] || 'text-gray-500 border-gray-200'}`}>
                          {u.is_active || '—'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-center">
                        <div
                          className="relative flex text-center justify-center"
                          ref={(el) => { menuRefs.current[u.id] = el; }}
                        >
                          <button
                            onClick={() => toggleMenu(u.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            aria-label="Actions"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="5" r="1.5" />
                              <circle cx="12" cy="12" r="1.5" />
                              <circle cx="12" cy="19" r="1.5" />
                            </svg>
                          </button>

                          {openMenuId === u.id && (
                            <div className="absolute right-0 top-full mt-1.5 w-44 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 py-1">
                              <button
                                onClick={() => { setOpenMenuId(null); navigate(`/users/${u.id}`); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                              >
                                <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Details
                              </button>
                              <button
                                onClick={() => { setOpenMenuId(null); navigate(`/users/${u.id}/edit`); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                              >
                                <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                Edit User
                              </button>
                              {isDirector && (
                                <>
                              {u.is_active === 'active' ? (
                                currentUser?.id !== u.id && (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setConfirmStatus({
                                        id: u.id,
                                        name: [u.first_name, u.last_name].filter(Boolean).join(' '),
                                        nextStatus: 'inactive',
                                      });
                                    }}
                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                    Deactivate User
                                  </button>
                                )
                              ) : (
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setConfirmStatus({
                                      id: u.id,
                                      name: [u.first_name, u.last_name].filter(Boolean).join(' '),
                                      nextStatus: 'active',
                                    });
                                  }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Activate User
                                </button>
                              )}
                              <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                              <button
                                onClick={() => { setOpenMenuId(null); setConfirmDelete(u.id); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete User
                              </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Showing{' '}
                  <span className="font-medium text-gray-700 dark:text-slate-300">
                    {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium text-gray-700 dark:text-slate-300">{pagination.total}</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let page;
                    if (pagination.total_pages <= 5) {
                      page = i + 1;
                    } else if (pagination.page <= 3) {
                      page = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      page = pagination.total_pages - 4 + i;
                    } else {
                      page = pagination.page - 2 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                          page === pagination.page
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}